// Verification level legend strings. Keep in sync with:
//   apps/verifier/src/ui/VerificationLevelLegend.tsx
//   apps/dashboard/src/components/VerificationLevelLegend.tsx
//   src/viewer.html (search: "what do these levels mean")
// Editing one without the others is a regression.

// Exported so a test can assert the copy verbatim and catch drift against
// the other two surfaces. The strings stay inline here (no shared module);
// the exports are only a test seam, not a shared source of truth.
export const PREAMBLE =
  "Verification levels summarize the strongest identity-anchored claim that holds for this receipt: integrity, then agent identity, then policy. Witnessed, approval, and counterparty are shown as separate badges because each is an independent fact about the receipt, not a rung on the level ladder.";

export const ROWS: { level: string; label: string; description: string }[] = [
  {
    level: "L0",
    label: "Integrity Only",
    description:
      "The receipt is well-formed and its action chain is internally consistent. No further claims hold."
  },
  {
    level: "L1",
    label: "Witnessed",
    description:
      "Every action was co-signed by the witness identified in the receipt. Agent identity is not bound."
  },
  {
    level: "L2",
    label: "Key Bound",
    description:
      "Every action carries a valid agent signature bound to the receipt's key. Whether that key is a vouched identity is the separate Identity signal (registered vs self-asserted)."
  },
  {
    level: "L3",
    label: "Policy Bound",
    description: "Key bound, and every action commits to the policy context the task declared."
  }
];

export const BADGES_PREAMBLE =
  "Badges are independent facts shown alongside the level. Approval and counterparty are reported as present but unverified until the signer's identity is registered and vouched, so they never raise the level on their own.";

export const BADGES: { badge: string; description: string }[] = [
  {
    badge: "Witnessed",
    description:
      "Every action was co-signed by an independent witness. Shown alongside any level, since witnessing is a property of the chain."
  },
  {
    badge: "Approval",
    description:
      "A party — human or agent — signed an approval bound to an action type in the chain."
  },
  {
    badge: "Counterparty",
    description: "A counterparty signed a confirmation bound to a recorded action and its evidence."
  }
];

export function VerificationLevelLegend() {
  return (
    <details className="level-legend">
      <summary className="level-legend-summary">What do these levels mean?</summary>
      <p className="level-legend-preamble">{PREAMBLE}</p>
      <div className="level-legend-table-wrap">
        <table className="level-legend-table">
          <thead>
            <tr>
              <th scope="col">Level</th>
              <th scope="col">Label</th>
              <th scope="col">Description</th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row) => (
              <tr key={row.level}>
                <td>{row.level}</td>
                <td>{row.label}</td>
                <td>{row.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="level-legend-preamble">{BADGES_PREAMBLE}</p>
      <div className="level-legend-table-wrap">
        <table className="level-legend-table">
          <thead>
            <tr>
              <th scope="col">Badge</th>
              <th scope="col">Meaning</th>
            </tr>
          </thead>
          <tbody>
            {BADGES.map((row) => (
              <tr key={row.badge}>
                <td>{row.badge}</td>
                <td>{row.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}
